import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ExternalLink, FileText, ArrowRight } from 'lucide-react'
import { RecordDetailPanel } from './record-detail-panel'
import { FormFillPanel } from './form-fill-panel'
import { EmptyState } from '@/components/shared/empty-state'
import { useMediaQuery } from '@/hooks/use-media-query'

const PANEL1_DEFAULT = 288
const PANEL1_MIN = 240
const PANEL1_MAX = 460
const PANEL1_KEY = 'records-shell-panel1-width'

const PANEL2_DEFAULT = 288
const PANEL2_MIN = 220
const PANEL2_MAX = 480
const PANEL2_KEY = 'records-shell-panel2-width'

function readStoredWidth(key: string, fallback: number, min: number, max: number): number {
  if (typeof window === 'undefined') return fallback
  const raw = window.localStorage.getItem(key)
  const n = raw ? Number(raw) : NaN
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback
}

interface RecordWorkspaceShellProps {
  projectId: string
  leftPanel: (ctx: {
    selectedRecordId: string | null
    onSelectRecord: (id: string) => void
    width: number
  }) => React.ReactNode
}

export function RecordWorkspaceShell({ projectId, leftPanel }: RecordWorkspaceShellProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const isNarrow = useMediaQuery('(max-width: 1023px)')

  const selectedRecordId = searchParams.get('record')
  const selectedFormId = searchParams.get('form')
  const selectedSubmissionId = searchParams.get('submission')

  // Resizable + persisted panel widths.
  const [panel1Width, setPanel1Width] = useState(() =>
    readStoredWidth(PANEL1_KEY, PANEL1_DEFAULT, PANEL1_MIN, PANEL1_MAX),
  )
  const [panel2Width, setPanel2Width] = useState(() =>
    readStoredWidth(PANEL2_KEY, PANEL2_DEFAULT, PANEL2_MIN, PANEL2_MAX),
  )

  useEffect(() => {
    try { window.localStorage.setItem(PANEL1_KEY, String(panel1Width)) } catch { /* ignore */ }
  }, [panel1Width])
  useEffect(() => {
    try { window.localStorage.setItem(PANEL2_KEY, String(panel2Width)) } catch { /* ignore */ }
  }, [panel2Width])

  const dragRef = useRef<
    | { startX: number; startWidth: number; min: number; max: number; setter: (w: number) => void }
    | null
  >(null)

  const startDrag = useCallback(
    (panel: 1 | 2) => (e: React.MouseEvent) => {
      e.preventDefault()
      dragRef.current =
        panel === 1
          ? { startX: e.clientX, startWidth: panel1Width, min: PANEL1_MIN, max: PANEL1_MAX, setter: setPanel1Width }
          : { startX: e.clientX, startWidth: panel2Width, min: PANEL2_MIN, max: PANEL2_MAX, setter: setPanel2Width }
      // Lock the cursor and suppress text selection for the duration of the drag.
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [panel1Width, panel2Width],
  )

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      // RTL: each panel sits to the RIGHT of its handle, so dragging the handle
      // left (positive delta) widens that panel.
      const delta = d.startX - e.clientX
      const next = Math.min(Math.max(d.startWidth + delta, d.min), d.max)
      d.setter(next)
    }
    const onMouseUp = () => {
      if (!dragRef.current) return
      dragRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const onSelectRecord = (recordId: string) => {
    setSearchParams({ record: recordId })
  }

  const onFormSelect = (formId: string, submissionId?: string) => {
    if (!selectedRecordId) return
    const params: Record<string, string> = { record: selectedRecordId, form: formId }
    if (submissionId) params.submission = submissionId
    setSearchParams(params)
  }

  const backToList = () => setSearchParams({})
  const backToRecord = () => {
    if (selectedRecordId) setSearchParams({ record: selectedRecordId })
  }

  // ─── Reusable panes ──────────────────────────────────────────────────────

  const formPane = (
    <div className="flex-1 h-full overflow-hidden flex flex-col min-w-0">
      {selectedRecordId && selectedFormId && (
        <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-1.5 border-b border-border bg-card">
          {isNarrow ? (
            <button
              onClick={backToRecord}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              بازگشت به پرونده
            </button>
          ) : <span />}
          <a
            href={`/projects/${projectId}/records/${selectedRecordId}/forms/${selectedFormId}${selectedSubmissionId ? `?submission=${selectedSubmissionId}` : ''}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            title="باز کردن فرم در تب جدید"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            باز کردن در تب جدید
          </a>
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        {selectedRecordId && selectedFormId ? (
          <FormFillPanel
            key={`${selectedRecordId}-${selectedFormId}-${selectedSubmissionId ?? 'new'}`}
            projectId={projectId}
            recordId={selectedRecordId}
            formId={selectedFormId}
            submissionId={selectedSubmissionId ?? undefined}
            onSaved={() => {}}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon={FileText}
              title="فرمی انتخاب نشده"
              description="یک فرم از پانل سمت راست انتخاب کنید"
            />
          </div>
        )}
      </div>
    </div>
  )

  // ─── Narrow: one pane at a time ────────────────────────────────────────────

  if (isNarrow) {
    if (selectedRecordId && selectedFormId) {
      return <div className="flex h-full overflow-hidden">{formPane}</div>
    }
    if (selectedRecordId) {
      return (
        <div className="flex flex-col h-full overflow-hidden">
          <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 border-b border-border bg-card">
            <button
              onClick={backToList}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              بازگشت به لیست
            </button>
          </div>
          <div className="flex-1 overflow-hidden flex">
            <RecordDetailPanel projectId={projectId} recordId={selectedRecordId} onFormSelect={onFormSelect} />
          </div>
        </div>
      )
    }
    return (
      <div className="h-full overflow-hidden">
        {leftPanel({ selectedRecordId, onSelectRecord, width: 0 })}
      </div>
    )
  }

  // ─── Wide: 3-pane resizable layout ─────────────────────────────────────────

  return (
    <div className="flex h-full overflow-hidden">
      {/* Panel 1 — Records list (resizable) */}
      <div style={{ width: panel1Width }} className="shrink-0 h-full">
        {leftPanel({ selectedRecordId, onSelectRecord, width: panel1Width })}
      </div>

      {/* Drag handle: Panel 1 / Panel 2 */}
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={startDrag(1)}
        className="group relative w-1.5 h-full shrink-0 cursor-col-resize bg-border hover:bg-primary/50 transition-colors active:bg-primary/70"
        title="برای تغییر اندازه لیست بکشید"
      >
        <span className="absolute inset-y-0 -inset-x-1.5" />
      </div>

      {/* Panel 2 — Record detail (resizable) */}
      {selectedRecordId ? (
        <RecordDetailPanel
          projectId={projectId}
          recordId={selectedRecordId}
          width={panel2Width}
          onFormSelect={onFormSelect}
        />
      ) : (
        <div
          style={{ width: panel2Width }}
          className="shrink-0 h-full border-e border-border flex items-center justify-center"
        >
          <p className="text-sm text-muted-foreground text-center px-4">
            یک سابقه را از لیست انتخاب کنید
          </p>
        </div>
      )}

      {/* Drag handle: Panel 2 / Panel 3 */}
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={startDrag(2)}
        className="group relative w-1.5 h-full shrink-0 cursor-col-resize bg-border hover:bg-primary/50 transition-colors active:bg-primary/70"
        title="برای تغییر اندازه بکشید"
      >
        <span className="absolute inset-y-0 -inset-x-1.5" />
      </div>

      {/* Panel 3 — Form fill */}
      {formPane}
    </div>
  )
}
